import React, { useState } from "react";
import { createPortal } from "react-dom";
import { HexColorPicker, HexColorInput } from "react-colorful";
import cn from "classnames";
import styles from "./dashboard-theme-editor.module.scss";
import {
  useControlledDropUp,
  DropUpRect,
} from "./use-drop-up";
import { ToolTip } from "@components/atoms/tooltip/tooltip";
import { IconButton } from "@components/atoms/buttons/icon-button/icon-button";
import { CheckBoxWithText } from "@components/atoms/check-box-with-text/check-box-with-text";
import { useI18N } from "@src/language/language-context";
import { DashboardColors } from "@src/types/dashboards";
import { DEFAULT_PALETTE } from "./palette-style";
import blueBrushIcon from "@src/assets/svg/blue-brush-icon.svg";
import closeIcon from "@src/assets/svg/close-icon.svg";

const cssClassName = "dashboard-theme-editor";

interface SwatchSpec {
  id: keyof DashboardColors;
  cssVar: string;
}

// Ten themable surfaces, in display order on the floating bar.
// Reads as two clean blocks: dashboard chrome (5) → widget text (3),
// with widget surfaces (2) sandwiched in between so the user sees
// the chrome they're sitting on, then the text they're going to
// edit, then the surface they paint text onto:
//   1. canvas_bg        — central-panel root bg;
//   2. subheader_bg     — dashboard header (title + draft/published chrome) bg;
//   3. text_primary     — dashboard primary text;
//   4. text_secondary   — dashboard secondary text;
//   5. widgets_bg       — widget tile bg;
//   6. border           — widget rest-state outline;
//   7. border_hover     — widget hover-state outline;
//   8. widget_title     — every widget's title text;
//   9. widget_primary   — every widget's body text (values, labels, units, tooltips);
//  10. widget_secondary — every widget's secondary text (timestamps + axis labels).
// Rebuild history: started with 6; the subheader was later added and
// slotted between the existing canvas_bg + widgets_bg; canvas_bg and
// widgets_bg were swapped so the panel-level chrome reads first; then
// the text_ slots were promoted above widgets_bg so the user
// encounters "dashboard chrome" → "dashboard text" → "widget surface"
// → "widget text" in a single left-to-right pass. The final shuffle
// added the three widget_ keys at the tail of the bar — they're the
// late-arrival controls and grouping them contiguously keeps the
// "dashboard vs widget" mental model intact.
// Must stay in lockstep with `DashboardColors` keys, swatchLabels /
// swatchDescriptions / popoverTitles lookup maps below, and
// `KEY_TO_CSS_VAR` in `palette-style.ts`.
const SWATCHES: readonly SwatchSpec[] = [
  { id: "canvas_bg", cssVar: "--color-cp-canvas-bg" },
  { id: "subheader_bg", cssVar: "--color-cp-subheader-bg" },
  { id: "text_primary", cssVar: "--color-cp-text-primary" },
  { id: "text_secondary", cssVar: "--color-cp-text-secondary" },
  { id: "widgets_bg", cssVar: "--color-cp-widgets-bg" },
  { id: "border", cssVar: "--color-cp-border" },
  { id: "border_hover", cssVar: "--color-cp-border-hover" },
  { id: "widget_title", cssVar: "--color-cp-widget-title" },
  { id: "widget_primary", cssVar: "--color-cp-widget-primary" },
  { id: "widget_secondary", cssVar: "--color-cp-widget-secondary" },
];

// Drop-up gap (the breathing room between the swatch and the popover),
// measured in CSS pixels. Only this is constant — the drop-up itself
// is auto-sized (it hugs its content: 200x200 px picker + ~32 px
// header + 16 px of padding), so the CSS dimensions live in the
// SCSS module rather than this math.
const DROP_UP_GAP = 8;

const computeDropUpStyle = (rect: DropUpRect): React.CSSProperties => ({
  // Anchor above the swatch with `transform: translate(-50%, -100%)`
  // — the browser does centering + lifting, no JS width/height math
  // needed. `position: fixed` because we portal to body; raw coords
  // from `getBoundingClientRect()` are already viewport-relative.
  left: Math.round(rect.left + rect.width / 2),
  position: "fixed",
  top: Math.round(rect.top - DROP_UP_GAP),
  transform: "translate(-50%, -100%)",
});

interface DashboardThemeEditorProps {
  currentColors: DashboardColors | null;
  // Called whenever the user picks a new color for any single key.
  // Phase 5 wires this to a debounced palette hook (immediate
  // optimistic dispatch + 400ms-debounced PATCH).
  onChange: (nextColors: DashboardColors) => void;
  // Called when the user clicks the Reset button. Phase 5 wires
  // this to an immediate PATCH with `{ colors: null }`.
  onReset: () => void;
  // Initial collapsed state. Defaults to true (just the brush icon
  // visible). Tests pass `false` to render the expanded card in
  // isolation; runtime users start with the brush and tap to
  // expand. Stays uncontrolled once mounted — toggling is via the
  // brush and `×` clicks.
  initialCollapsed?: boolean;
}

export const DashboardThemeEditor = ({
  currentColors,
  onChange,
  onReset,
  initialCollapsed = true,
}: DashboardThemeEditorProps) => {
  const i18n = useI18N();
  const resetText = i18n("modules.dashboardThemeEditor.resetPalette");
  const titleText = i18n("modules.dashboardThemeEditor.editorTitle");
  const brushTooltip = i18n("modules.dashboardThemeEditor.brushTooltip");
  const closeAlt = i18n("modules.dashboardThemeEditor.closeAriaLabel");
  const hexCheckboxLabel = i18n(
    "modules.dashboardThemeEditor.hexModeCheckbox",
  );
  const swatchLabels = {
    canvas_bg: i18n("modules.dashboardThemeEditor.swatchLabels.canvasBg"),
    subheader_bg: i18n(
      "modules.dashboardThemeEditor.swatchLabels.subheaderBg",
    ),
    text_primary: i18n(
      "modules.dashboardThemeEditor.swatchLabels.textPrimary",
    ),
    text_secondary: i18n(
      "modules.dashboardThemeEditor.swatchLabels.textSecondary",
    ),
    widgets_bg: i18n("modules.dashboardThemeEditor.swatchLabels.widgetsBg"),
    border: i18n("modules.dashboardThemeEditor.swatchLabels.border"),
    border_hover: i18n(
      "modules.dashboardThemeEditor.swatchLabels.borderHover",
    ),
    widget_title: i18n(
      "modules.dashboardThemeEditor.swatchLabels.widgetTitle",
    ),
    widget_primary: i18n(
      "modules.dashboardThemeEditor.swatchLabels.widgetPrimary",
    ),
    widget_secondary: i18n(
      "modules.dashboardThemeEditor.swatchLabels.widgetSecondary",
    ),
  };
  // Per-swatch explanation surfaced on hover. Same ten keys as
  // `swatchLabels` so the lookup path is symmetric.
  const swatchDescriptions = {
    canvas_bg: i18n(
      "modules.dashboardThemeEditor.swatchDescriptions.canvasBg",
    ),
    subheader_bg: i18n(
      "modules.dashboardThemeEditor.swatchDescriptions.subheaderBg",
    ),
    text_primary: i18n(
      "modules.dashboardThemeEditor.swatchDescriptions.textPrimary",
    ),
    text_secondary: i18n(
      "modules.dashboardThemeEditor.swatchDescriptions.textSecondary",
    ),
    widgets_bg: i18n(
      "modules.dashboardThemeEditor.swatchDescriptions.widgetsBg",
    ),
    border: i18n(
      "modules.dashboardThemeEditor.swatchDescriptions.border",
    ),
    border_hover: i18n(
      "modules.dashboardThemeEditor.swatchDescriptions.borderHover",
    ),
    widget_title: i18n(
      "modules.dashboardThemeEditor.swatchDescriptions.widgetTitle",
    ),
    widget_primary: i18n(
      "modules.dashboardThemeEditor.swatchDescriptions.widgetPrimary",
    ),
    widget_secondary: i18n(
      "modules.dashboardThemeEditor.swatchDescriptions.widgetSecondary",
    ),
  };
  // Per-key heading rendered above the picker inside the drop-up
  // card. Action-oriented ("Choose X") rather than descriptive
  // ("Background color behind widgets") — the picker is the
  // affordance, the title is its label.
  const popoverTitles = {
    canvas_bg: i18n(
      "modules.dashboardThemeEditor.popoverTitles.canvasBg",
    ),
    subheader_bg: i18n(
      "modules.dashboardThemeEditor.popoverTitles.subheaderBg",
    ),
    text_primary: i18n(
      "modules.dashboardThemeEditor.popoverTitles.textPrimary",
    ),
    text_secondary: i18n(
      "modules.dashboardThemeEditor.popoverTitles.textSecondary",
    ),
    widgets_bg: i18n(
      "modules.dashboardThemeEditor.popoverTitles.widgetsBg",
    ),
    border: i18n("modules.dashboardThemeEditor.popoverTitles.border"),
    border_hover: i18n(
      "modules.dashboardThemeEditor.popoverTitles.borderHover",
    ),
    widget_title: i18n(
      "modules.dashboardThemeEditor.popoverTitles.widgetTitle",
    ),
    widget_primary: i18n(
      "modules.dashboardThemeEditor.popoverTitles.widgetPrimary",
    ),
    widget_secondary: i18n(
      "modules.dashboardThemeEditor.popoverTitles.widgetSecondary",
    ),
  };

  // Single source of truth for the open swatch. Enforces "at most one
  // drop-up open at a time" via a single shared `useState` (no
  // cross-component coordination needed).
  const [openSwatchId, setOpenSwatchId] = useState<keyof DashboardColors | null>(
    null,
  );

  // Default state is COLLAPSED — a 48x48 circle with the brush icon.
  // Click expands to the full editor (title + Reset + 10 swatches).
  // Click `×` collapses back. State lives inside the editor so the
  // parent (`DashboardsPage`) doesn't have to know about the toggle.
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);

  const handleExpand = () => {
    setIsCollapsed(false);
  };
  const handleCollapse = () => {
    // Reset any open drop-up when collapsing — the swatches unmount,
    // and re-expanding with a stale `openSwatchId` would resurrect
    // a drop-up anchored to an anchor element that's been replaced.
    setOpenSwatchId(null);
    setIsCollapsed(true);
  };

  return (
    <div
      className={cn(styles[`${cssClassName}`], {
        [styles[`${cssClassName}--collapsed`]]: isCollapsed,
      })}
    >
      {isCollapsed ? (
        /* Brush trigger — the existing `<IconButton>` atom (24x24,
         * variant `edit-and-close-icon`) centered inside the
         * 48x48 circle wrapper. No bespoke click handler — clicking
         * the brush expands the editor. */
        <div className={styles[`${cssClassName}__trigger`]}>
          <IconButton
            fun={handleExpand}
            icon={blueBrushIcon}
            variant="edit-and-close-icon"
            tooltip={brushTooltip}
            tooltipPosition="left"
          />
        </div>
      ) : (
        /* Expanded — mirrors `tickets-taskbar-card.tsx` layout:
         * a row with [light content card on the left] + [small X-
         * button on the right that sits in the dark outer frame],
         * both inside the dark blurred rounded outer. The X is
         * structurally NOT a floating disc — it's a sibling of the
         * content card. */
        <div className={styles[`${cssClassName}__inner`]}>
          <div className={styles[`${cssClassName}__card`]}>
            <div className={styles[`${cssClassName}__title`]}>{titleText}</div>
            <div className={styles[`${cssClassName}__bar`]}>
              <button
                type="button"
                className={styles[`${cssClassName}__reset`]}
                onClick={onReset}
              >
                {resetText}
              </button>
              <div
                className={styles[`${cssClassName}__divider`]}
                aria-hidden="true"
              />
              {SWATCHES.map((spec) => (
                <Swatch
                  key={spec.id}
                  spec={spec}
                  currentValue={currentColors?.[spec.id] ?? null}
                  label={swatchLabels[spec.id]}
                  description={swatchDescriptions[spec.id]}
                  title={popoverTitles[spec.id]}
                  hexCheckboxLabel={hexCheckboxLabel}
                  isOpen={openSwatchId === spec.id}
                  onOpen={() => setOpenSwatchId(spec.id)}
                  onClose={() =>
                    setOpenSwatchId((prev) => (prev === spec.id ? null : prev))
                  }
                  // Emit the FULL palette, not a single-key update —
                  // the reducer replaces wholesale (tested in
                  // dashboards-slice). A one-key emit would silently
                  // wipe the other five user-set colors on every pick.
                  onChange={(value) =>
                    onChange({ ...(currentColors ?? {}), [spec.id]: value })
                  }
                />
              ))}
            </div>
          </div>
          <div className={styles[`${cssClassName}__icon`]}>
            <button
              type="button"
              className={styles[`${cssClassName}__icon-button`]}
              onClick={handleCollapse}
              aria-label={closeAlt}
            >
              <img src={closeIcon} alt="" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

interface SwatchProps {
  spec: SwatchSpec;
  currentValue: string | null;
  label: string;
  description: string;
  // Action-oriented title rendered above the picker once the
  // drop-up is open. Fed from `popoverTitles`. Distinct from
  // `label` (which is the button aria-label).
  title: string;
  // Label for the "type HEX" checkbox inside the drop-up. Off by
  // default; clicking reveals the typed-hex input below the picker.
  hexCheckboxLabel: string;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  // Wire to HexColorPicker's onChange. Streams a new hex on every
  // drag tick; the upstream debounce hook coalesces into one PATCH.
  onChange: (newValue: string) => void;
}

const Swatch = ({
  spec,
  currentValue,
  label,
  description,
  title,
  hexCheckboxLabel,
  isOpen,
  onOpen,
  onClose,
  onChange,
}: SwatchProps) => {
  const { anchorRef, dropUpRef, anchorRect, toggle } = useControlledDropUp({
    isOpen,
    onOpen,
    onClose,
  });

  // Swatch shows the user's picked color when set; otherwise the
  // project default — so the user sees what their surface would
  // look like right now, not a blank token. `canvas_bg` is the
  // literal `#131b20` (matches the gradient-border fill behind the
  // central panel); the rest of the keys mirror the SCSS fallbacks
  // in `palette-style.ts`.
  const displayColor = currentValue ?? DEFAULT_PALETTE[spec.id];

  // Hex-input mode is opt-in via a checkbox in the drop-up; we
  // reset on close so re-opening a different swatch (or re-opening
  // the same one) starts with the picker-only view. While the
  // input is open we DO NOT mirror the in-progress typed value
  // into the picker — the user requirement is "show the new colour
  // only at full 6 digits, otherwise nothing". The picker therefore
  // keeps showing whichever upstream value it already had; only
  // when the typed value reaches 6 chars does the onChange fire
  // and the picker re-paints via the new `currentValue` flowing
  // back from the optimistic Redux update (see `handleHexChange`).
  const [isHexInputShown, setIsHexInputShown] = React.useState(false);
  React.useEffect(() => {
    if (!isOpen) setIsHexInputShown(false);
  }, [isOpen]);

  // The picker reflects ONLY upstream-published colours. There is
  // no local "draft" buffer — picking and typing are both routed
  // through the parent and back via Redux, so this single source
  // is authoritative for whatever the user has committed.
  const pickerColor = currentValue ?? DEFAULT_PALETTE[spec.id];
  // Picker drag commits on every tick (existing behaviour — a drag
  // delta is small and the user has direct visual feedback on each
  // pick).
  const handlePickerChange = (hex: string) => {
    onChange(hex);
  };
  // Hex-input commits ONLY when the user reaches a complete 6-digit
  // hex. Mid-typing (1-5 chars, or any 3-char short hex the lib
  // considers "valid") is dropped on the floor: the picker stays
  // on its previous value, the upstream palette is untouched. The
  // moment the user types the 6th char, the lib fires onChange
  // with a fully-formed `#RRGGBB`, our gate passes, upstream
  // optimistic dispatch updates `currentValue`, and the picker
  // re-paints from the new value. No `onBlur` hook — the commit
  // fires inline on length-6, so unmounting mid-typing is safe (a
  // 5-char draft never reaches BE in the first place).
  const handleHexChange = (hex: string) => {
    if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      onChange(hex);
    }
  };

  return (
    <>
      <ToolTip content={description} position="top-left">
        <button
          ref={anchorRef}
          type="button"
          className={cn(styles[`${cssClassName}__swatch`], {
            [styles[`${cssClassName}__swatch--default`]]: currentValue == null,
          })}
          style={{ backgroundColor: displayColor }}
          aria-label={label}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          onClick={toggle}
        />
      </ToolTip>
      {isOpen &&
        anchorRect &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={dropUpRef}
            className={styles[`${cssClassName}__drop-up`]}
            style={computeDropUpStyle(anchorRect)}
          >
            <div className={styles[`${cssClassName}__drop-up-header`]}>
              {title}
            </div>
            <div className={styles[`${cssClassName}__picker`]}>
              <HexColorPicker
                // react-colorful requires a color prop. When the user
                // hasn't picked yet (`currentValue === null`) we seed
                // with the SCSS default so the picker's hue point sits
                // near the actual paint. `DEFAULT_PALETTE.canvas_bg`
                // is the literal `#131b20` (matches the gradient-
                // border fill visible behind the central panel) so
                // the unthemed canvas doesn't show as a misleading
                // transparent token. The picker reads upstream only
                // — mid-typing in the hex input does not seed its
                // hue point until the full 6-char hex lands (see
                // `handleHexChange`).
                color={pickerColor}
                onChange={handlePickerChange}
              />
            </div>
            <div
              className={styles[`${cssClassName}__hex-mode`]}
            >
              <CheckBoxWithText
                text={hexCheckboxLabel}
                checkboxState={isHexInputShown}
                handleCheckBoxClick={() => setIsHexInputShown((prev) => !prev)}
              />
            </div>
            {isHexInputShown && (
              <div className={styles[`${cssClassName}__hex-input`]}>
                <HexColorInput
                  // Same `pickerColor` source as the visual picker so
                  // the two stay in lockstep across the session. The
                  // local `handleHexChange` gates upstream commits to
                  // a full 6-digit hex — the BE only sees the user's
                  // "I'm done" signal, mid-typing is silently dropped.
                  // `prefixed` is on so the field shows "# RRGGBB";
                  // alpha stays at default (off) — palette is opaque
                  // hex only.
                  prefixed
                  color={pickerColor}
                  onChange={handleHexChange}
                />
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
};
